import { useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { api, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const RADII = [10, 25, 50, 100, 250, 0];
const TXT = {
  en: { email: "Your email", radius: "How far from", anywhere: "Anywhere in Canada", news: "Also send me the newsletter",
    btn: "Alert me", thanks: "Done! We'll email you a daily summary of new matches.", what: (b) => `Daily email of new ${b} listings`, km: "within" },
  fr: { email: "Votre courriel", radius: "Distance de", anywhere: "Partout au Canada", news: "M'envoyer aussi l'infolettre",
    btn: "Alertez-moi", thanks: "C'est fait ! Vous recevrez un résumé quotidien des nouvelles annonces.", what: (b) => `Courriel quotidien des nouvelles annonces ${b}`, km: "à moins de" },
};

export function LikeThisSignup({ p, lang, preview, onDone }) {
  const t = TXT[lang] || TXT.en;
  const { user } = useAuth();
  const ctx = p._ctx || { breed: "Golden Retriever", city: "Ottawa" };
  const pick = (k) => (lang === "fr" ? p[`${k}_fr`] : p[k]);
  const [f, setF] = useState({ email: user?.email || "", radius_km: 50, newsletter: false, website: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const set = (k, v) => { setF((x) => ({ ...x, [k]: v })); setErr(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (preview || !p._ctx) return;
    setBusy(true);
    try {
      await api.post(`/listings/${ctx.id}/like-this`, { ...f, lang, ab_id: p._ab || "", variant: p._v || "" });
      setDone(true);
      onDone?.();
    } catch (e2) { setErr(errText(e2)); }
    finally { setBusy(false); }
  };

  if (done) return <p className="mt-5 flex items-center gap-2 text-emerald-700 font-semibold" data-testid="popup-signup-thanks"><CheckCircle2 size={20} className="shrink-0" /> {pick("signup_thanks") || t.thanks}</p>;
  return (
    <form onSubmit={submit} className="mt-5 space-y-3" data-testid="popup-like-form">
      <p className="text-xs font-semibold text-slate-500" data-testid="popup-like-what">{t.what(ctx.breed)}</p>
      <Input type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} placeholder={t.email} className="h-11 rounded-xl" data-testid="popup-like-email" />
      <input tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden="true" />
      <Select value={String(f.radius_km)} onValueChange={(v) => set("radius_km", Number(v))}>
        <SelectTrigger className="h-11 rounded-xl" data-testid="popup-like-radius"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white">
          {RADII.map((r) => <SelectItem key={r} value={String(r)} data-testid={`popup-like-radius-${r}`}>{r ? (ctx.city ? `${t.km} ${r} km — ${ctx.city}` : `${r} km`) : t.anywhere}</SelectItem>)}
        </SelectContent>
      </Select>
      {p.signup_newsletter && (
        <label className="flex items-center gap-2.5 text-sm text-[#12234A] cursor-pointer">
          <Checkbox checked={f.newsletter} onCheckedChange={(v) => set("newsletter", !!v)} data-testid="popup-like-newsletter" className="data-[state=checked]:bg-[#F5551E] data-[state=checked]:border-[#F5551E]" />
          {t.news}
        </label>
      )}
      {err && <p className="text-sm text-red-600" data-testid="popup-signup-error">{err}</p>}
      <button type="submit" disabled={busy} data-testid="popup-like-submit" className="w-full h-11 rounded-full bg-[#12234A] hover:bg-[#1C3166] text-white font-semibold transition-colors flex items-center justify-center">
        {busy ? <Loader2 size={18} className="animate-spin" /> : pick("signup_button_text") || t.btn}
      </button>
    </form>
  );
}
